import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid2,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    TextField,
    Tooltip,
    Box,
    Chip
  } from '@mui/material';
  import { getAllErrorLogs} from 'services/useractivity.service';
  import { DragPreview } from 'components/third-party/ReactTable';
  import { useEffect, useMemo, useState } from 'react';
  import { getTimeLine } from 'store/reducers/users';
  import { dispatch } from 'store';
  import { DndProvider } from 'react-dnd';
  import { HTML5Backend } from 'react-dnd-html5-backend';
  import Data404 from 'components/third-party/Data404';
  import CustomTable from 'components/@extended/CustomTable';
  import { Calendar, Copy, Timer } from 'iconsax-react';
  import SkeletonTable from 'components/SkeletonTable';
  import MainCard from 'components/MainCard';
  import { FormattedMessage, useIntl } from 'react-intl';
  import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
  import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
  import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
  
  const ErrorLogs = (props: any) => {
    const intl = useIntl();
    const [pageSize, setPageSize] = useState(10);

    const [currentPage, setCurrentPage] = useState(1);
    const [count, setCount] = useState(1);
    const [level, setLevel] = useState('error');
    const [errorLogsData, setErrorLogsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(true);
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());
  
    const handleDateChange = (date: any, type: any) => {
      if (type === 'start') {
        setStartDate(date);
      } else {
        setEndDate(date);
      }
    };
  
    const handlePageChange = (event: any, page: number) => {
      setCurrentPage(page);
    };
    const getErrorLogs = async () => {
      dispatch(getTimeLine([]));
      setLoading(true);
      getAllErrorLogs({ limit: pageSize, skip: currentPage, startDate: startDate, endDate: endDate, level })
        .then((response) => {
          setCount(response.data.data.count);
          setLoading(false);
          setErrorLogsData(response.data.data?.logs);
          
          setLoading(false);
          if (response.data.newArray?.length > 0) setNotFound(false);
        })
        .catch((err) => {
          console.log(err);
          setLoading(false);
        });
    };
    useEffect(() => {
      getErrorLogs();
    }, [currentPage, pageSize, level, startDate, endDate]);
  
    const StackCell = ({ value }: any) => {
      const [open, setOpen] = useState(false);
      // Function to copy stack trace
      const copyToClipboard = () => {
        navigator.clipboard.writeText(value);
        alert('Stack trace copied to clipboard!');
      };
  
      return (
        <>
          {/* Tooltip for full text on hover */}
          {value ? (
            <>
              <Tooltip title={value} arrow>
                <div
                  style={{
                    maxWidth: '200px',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    cursor: 'pointer'
                  }}
                  onClick={() => setOpen(true)}
                >
                  {value}
                </div>
              </Tooltip>
              <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Stack Detail:</DialogTitle>
                <DialogContent>
                  <pre style={{ whiteSpace: 'pre-wrap' }}>{value}</pre>
                </DialogContent>
                <DialogActions>
                  <IconButton
                    sx={{
                      position: 'absolute',
                      top: '10px',
                      right: '10px',
                      cursor: 'pointer',
                      background: 'transparent',
                      border: 'none'
                    }}
                    onClick={copyToClipboard}
                  >
                    <Copy />
                  </IconButton>
                  {/* <Button onClick={copyToClipboard}>Copy</Button> */}
                  <Button variant="contained" color="error" onClick={() => setOpen(false)}>
                    Close
                  </Button>
                </DialogActions>
              </Dialog>
            </>
          ) : (
            '--'
          )}
        </>
      );
    };
  
   
    const columns = useMemo(
      () => [
        {
          Header: 'severity',
          accessor: 'level',
          Cell: ({ value }: { value: string }) => {
            let color: 'error' | 'info' | 'success' | 'default' | 'primary' | 'secondary' | 'warning' = 'default';
  
            // Determine the color based on the message type
            if (value.toLowerCase().includes('error')) {
              color = 'error';
            } else if (value.toLowerCase().includes('warn')) {
              color = 'warning';
            }
            return <Chip color={color} label={value} size="small" variant="light" sx={{ my: 1 }} />;
          }
        },
        {
          Header: 'Stack',
          accessor: 'meta.stack',
          Cell: ({ value }: any) => <StackCell value={value} />
        },
        {
          Header: 'Time',
          accessor: 'timestamp',
          Cell: ({ value }) => new Date(value).toLocaleString()
        },
  
        {
          Header: 'Description',
          accessor: 'message'
        }
      ],
      []
    );
  
    const handleChange = (event: SelectChangeEvent<number>) => {
      setPageSize(+event.target.value);
      setCurrentPage(1);
    };
    return (
      <Box display={'flex'} flexDirection={'column'} justifyContent={'space-between'} gap={'5px'}>

      <MainCard
        title={
          <span style={{ display: 'flex', alignItems: 'center', fontSize: '18px', fontWeight: 600 }}>
            <span style={{ marginLeft: 5 }}>
              <FormattedMessage id="Error Logs" />
            </span>
          </span>
        }
        content={false}
        sx={{
          '& .MuiCardHeader-root': {
            padding: '7px 13px'
          }
        }}
      >
        </MainCard>
        <MainCard content = {false}>
        <Stack alignItems="center" direction="row-reverse" justifyContent="space-between" sx={{ py: 1, mr: 2 }}>
          <Stack direction={'row'} spacing={1} mt={1} mb={1}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Stack spacing={1}>
                <InputLabel htmlFor="cal-startDate-date">
                  <FormattedMessage id="Start Date" />
                </InputLabel>
                <MobileDatePicker
                  value={startDate}
                  format="dd/MM/yyyy"
                  onChange={(date) => handleDateChange(date, 'start')}
                  // disabled={disabled}
                  closeOnSelect={true}
                  // maxDate={endDate}
                  slotProps={{
                    textField: {
                      id: 'cal-startDate-date',
                      placeholder: intl.formatMessage({ id: 'Start Date' }),
                      fullWidth: true,
                      InputProps: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <Calendar />
                          </InputAdornment>
                        )
                      }
                    }
                  }}
                />
              </Stack>
              <Stack spacing={1}>
                <InputLabel htmlFor="cal-endDate-date">
                  <FormattedMessage id="End Date" />
                </InputLabel>
                <MobileDatePicker
                  value={endDate}
                  format="dd/MM/yyyy"
                  onChange={(date) => handleDateChange(date, 'end')}
                  closeOnSelect={true}
                  minDate={startDate}
                  disabled={!startDate}
                  slotProps={{
                    textField: {
                      id: 'cal-endDate-date',
                      placeholder: intl.formatMessage({ id: 'End Date' }),
                      fullWidth: true,
                      InputProps: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <Calendar />
                          </InputAdornment>
                        )
                      }
                    }
                  }}
                />
              </Stack>
            </LocalizationProvider>
          </Stack>
          <Stack sx={{ alignItems: 'center' }} ml={2.5}>
            
            <Stack spacing={1}>
              <InputLabel>
                <FormattedMessage id="Level" />
              </InputLabel>
              <Select
                labelId="level"
                id="level"
                value={level}
                onChange={(event: any) => {
                  setLevel(event.target.value);
                }}
              >
                <MenuItem value="">
                  <em>
                    <FormattedMessage id="Select Level" />
                  </em>
                </MenuItem>
                <MenuItem value={'error'}>
                  <FormattedMessage id="Error" />
                </MenuItem>
                <MenuItem value={'warn'}>
                  <FormattedMessage id="Warn" />
                </MenuItem>
              </Select>
            </Stack>
          </Stack>
       
        </Stack>
        <Divider />
        <Grid2 container spacing={1}>
          {loading ? (
            <Grid2 size = {12}>
              <SkeletonTable columns={columns} />
            </Grid2>
          ) : (
            <Grid2 size = {12}>
              {errorLogsData && errorLogsData.length > 0 ? (
                <DndProvider backend={HTML5Backend}>
                  {errorLogsData && (
                    <CustomTable
                      icon={Timer}
                      text="No timeline to display"
                      sortArray={[]}
                      columns={columns}
                      data={errorLogsData || []}
                      sort={[]}
                      setSortingData={null}
                      notFound={notFound}
                      search={''}
                      count={count}
                      size={pageSize}
                      pageIndex={currentPage}
                      handlePageChange={handlePageChange}
                      handleSizeChange={handleChange}
                    />
                  )}
                  <DragPreview />
                </DndProvider>
              ) : (
                <Data404 title="No Data Available" />
              )}
            </Grid2>
          )}
        </Grid2>
      </MainCard>
      </Box>
    );
  };
  
  export default ErrorLogs;
  